import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type AdhocCall, type FlowVersion, type Voice } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";
import { buildQuickMessageFlow } from "./quickMessageFlow";
import { loadLastDestination, saveLastDestination } from "./lastDestinationStorage";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

const POLL_INTERVAL_MS = 1000;

/**
 * Triggers one real outbound call against an already-published flow version
 * (docs/PROGRESS.md Slice AY, P1's "real outbound voice call ... via flow
 * builder"). The API process only records the request — cmd/worker's own
 * poll loop (RunAdhocCallDispatcher) claims it and places the real call, the
 * same durable-state path every other outbound call in this system already
 * goes through. This panel polls the request's own status until the
 * dispatcher has acted, rather than pretending the call happens
 * synchronously.
 *
 * Gated to tenant_admin only (tenant.ActionPlaceAdhocCall) — narrower than
 * ReferencesPanel, which operators can also use — because dialing an
 * arbitrary destination on demand is a more direct toll-fraud exposure than
 * campaign operations, which are bounded by a pre-imported recipient list.
 */
type Mode = "existing" | "quick_message";

export function AdhocCallPanel() {
  const [mode, setMode] = useState<Mode>("quick_message");
  const [flowVersions, setFlowVersions] = useState<FlowVersion[]>([]);
  const [flowVersionId, setFlowVersionId] = useState("");
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceKey, setVoiceKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [destination, setDestination] = useState(() => loadLastDestination(window.localStorage));
  const [trunkRef, setTrunkRef] = useState("signalwire-trunk");
  const [callerId, setCallerId] = useState("");
  const [request, setRequest] = useState<AdhocCall | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listFlowVersions()
      .then(({ flow_versions }) => {
        if (cancelled) return;
        setFlowVersions(flow_versions);
        const first = flow_versions[0];
        if (first) setFlowVersionId((current) => current || first.flow_version_id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeError(err));
      });
    api
      .listVoices()
      .then(({ voices }) => {
        if (cancelled) return;
        setVoices(voices);
        const first = voices[0];
        if (first) setVoiceKey((current) => current || first.voice + "\u0000" + first.language);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!request || request.status !== "pending") return;
    const id = request.adhoc_call_id;
    const timer = window.setInterval(() => {
      api
        .getAdhocCall(id)
        .then((updated) => setRequest((current) => (current?.adhoc_call_id === id ? updated : current)))
        .catch(() => {
          /* a transient poll failure is not worth surfacing; the next tick retries */
        });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [request]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setRequest(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      let targetFlowVersionId = flowVersionId;
      if (mode === "quick_message") {
        const [voice, language] = voiceKey.split("\u0000");
        if (!voice || !language) throw new Error("register a voice first (above) to use a quick message");
        const doc = buildQuickMessageFlow(messageText, voice, language);
        const published = await api.publishFlow(csrfToken, "Quick message " + new Date().toISOString(), doc);
        targetFlowVersionId = published.flow_version_id;
      }
      const placed = await api.placeAdhocCall(csrfToken, targetFlowVersionId, destination, trunkRef, callerId);
      setRequest(placed);
      saveLastDestination(window.localStorage, destination);
      if (mode === "quick_message") {
        const { flow_versions } = await api.listFlowVersions();
        setFlowVersions(flow_versions);
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = mode === "existing" ? flowVersionId !== "" : messageText.trim() !== "" && voiceKey !== "";

  return (
    <section aria-labelledby="adhoc-call-heading" className="card">
      <h2 id="adhoc-call-heading">Place a call</h2>
      <p className="card__note">
        Triggers one real outbound call. cmd/worker's own dispatcher places it within a few
        seconds of the request below — this does not dial synchronously. Quick message builds a
        minimal flow (start → tts → hangup) from your text and a registered voice, publishes it,
        then places the call — the same publish/place path an authored flow already uses.
      </p>

      <div className="field" role="radiogroup" aria-label="Message source">
        <label>
          <input
            type="radio"
            name="adhoc-call-mode"
            checked={mode === "quick_message"}
            onChange={() => setMode("quick_message")}
          />
          Quick message (type text, rendered via TTS)
        </label>
        <label>
          <input
            type="radio"
            name="adhoc-call-mode"
            checked={mode === "existing"}
            onChange={() => setMode("existing")}
          />
          Existing published flow
        </label>
      </div>

      <form className="invites__form" onSubmit={(event) => void submit(event)}>
        {mode === "quick_message" ? (
          <>
            <label className="field">
              <span>Message</span>
              <textarea
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                placeholder="What should this call say?"
                required
              />
            </label>
            <label className="field">
              <span>Voice</span>
              <select value={voiceKey} onChange={(event) => setVoiceKey(event.target.value)} required>
                {voices.length === 0 ? <option value="">No voices registered yet (see above)</option> : null}
                {voices.map((v) => (
                  <option key={v.voice + v.language} value={v.voice + "\u0000" + v.language}>
                    {v.voice} ({v.language})
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="field">
            <span>Flow version</span>
            <select value={flowVersionId} onChange={(event) => setFlowVersionId(event.target.value)} required>
              {flowVersions.length === 0 ? <option value="">No published flow versions yet</option> : null}
              {flowVersions.map((v) => (
                <option key={v.flow_version_id} value={v.flow_version_id}>
                  {v.name} ({v.flow_version_id.slice(0, 8)})
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>Destination (E.164)</span>
          <input
            type="text"
            placeholder="+15550100000"
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Trunk</span>
          <input type="text" value={trunkRef} onChange={(event) => setTrunkRef(event.target.value)} required />
        </label>
        <label className="field">
          <span>Caller ID (optional)</span>
          <input type="text" value={callerId} onChange={(event) => setCallerId(event.target.value)} />
        </label>
        <button type="submit" disabled={submitting || !canSubmit}>
          Place call
        </button>
      </form>

      {error ? (
        <p className="card__error" role="alert">
          {error}
        </p>
      ) : null}

      {request ? (
        <p className="card__note" role="status">
          {request.status === "pending" ? `Request ${request.adhoc_call_id} is pending…` : null}
          {request.status === "dispatched" ? `Dispatched — call id ${request.call_id}` : null}
          {request.status === "failed" ? `Failed: ${request.error}` : null}
        </p>
      ) : null}
    </section>
  );
}
